import { useState, useEffect } from "react";
import axios from "axios";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Droplets, Truck, Calendar as CalendarIcon, MapPin, CheckCircle2, Clock,
  Phone, Mail, Star, ChevronDown, Sparkles, GlassWater, Repeat, Copy
} from "lucide-react";
import { API, TIME_SLOTS } from "../lib/constants";
import { LiveClock } from "../components/LiveClock";
import { Logo } from "../components/Logo";

// Success message auto-hide delay after booking confirmation (ms)
const SUCCESS_MESSAGE_HIDE_MS = 15000;

export const LandingPage = () => {
  const [nome, setNome] = useState("");
  const [indirizzo, setIndirizzo] = useState("");
  const [tipoAcqua, setTipoAcqua] = useState("");
  const [dataConsegna, setDataConsegna] = useState(null);
  const [oraConsegna, setOraConsegna] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [ricorrenza, setRicorrenza] = useState("nessuna");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [slotAvailability, setSlotAvailability] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonianze`)
      .then((r) => setTestimonials(r.data))
      .catch(() => { /* fallback silenzioso: testimonial non critici */ });
  }, []);

  useEffect(() => {
    if (dataConsegna) {
      axios.get(`${API}/disponibilita/${format(dataConsegna, "yyyy-MM-dd")}`)
        .then((r) => setSlotAvailability(r.data))
        .catch(() => setSlotAvailability([]));
    }
  }, [dataConsegna]);

  const getSlotInfo = (time) => {
    const slot = slotAvailability.find((s) => s.ora === time);
    return slot || { disponibile: true, posti_disponibili: 4 };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nome || !indirizzo || !tipoAcqua || !dataConsegna || !oraConsegna) {
      toast.error("Per favore compila tutti i campi obbligatori");
      return;
    }
    const slotInfo = getSlotInfo(oraConsegna);
    if (!slotInfo.disponibile) {
      toast.error(`L'orario ${oraConsegna} è al completo. Scegli un altro orario.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/prenotazioni`, {
        nome, indirizzo, tipo_acqua: tipoAcqua,
        data_consegna: format(dataConsegna, "yyyy-MM-dd"),
        ora_consegna: oraConsegna,
        telefono: telefono || null,
        email: email || null,
        ricorrenza: ricorrenza,
        note: null
      });
      setSuccessData(res.data);
      setShowSuccess(true);
      toast.success("Prenotazione confermata! Ti aspettiamo.");
      setNome(""); setIndirizzo(""); setTipoAcqua(""); setDataConsegna(null);
      setOraConsegna(""); setTelefono(""); setEmail(""); setRicorrenza("nessuna");
      setSlotAvailability([]);
      setTimeout(() => { setShowSuccess(false); setSuccessData(null); }, SUCCESS_MESSAGE_HIDE_MS);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la prenotazione");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToBooking = () =>
    document.getElementById("prenota")?.scrollIntoView({ behavior: "smooth" });

  const disabledDays = (date) =>
    isBefore(date, addDays(startOfDay(new Date()), 1));

  return (
    <div>
      <nav className="fixed top-0 left-0 right-0 z-50 glass px-4 py-3" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size={36} />
            <span className="font-bold text-xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Acqua Vicina
            </span>
          </div>
          <div className="hidden md:block">
            <LiveClock className="text-slate-600 text-sm" />
          </div>
          <Button onClick={scrollToBooking} className="btn-primary text-white rounded-full px-6" data-testid="nav-prenota-btn">
            Prenota Ora
          </Button>
        </div>
      </nav>

      <section className="hero-section pt-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="location-badge mb-6">
                <MapPin className="w-4 h-4" />
                Cascina, Pisa
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Acqua Fresca a <span className="gradient-text">Domicilio</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg leading-relaxed">
                Servizio di consegna acqua in bottiglia per i residenti nel comune di Cascina.
                Tu fornisci le bottiglie vuote da 1,5L, noi le riempiamo con acqua fresca.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={scrollToBooking} className="btn-primary text-white rounded-full px-8 py-6 text-lg" data-testid="hero-prenota-btn">
                  <CalendarIcon className="w-5 h-5 mr-2" /> Prenota Ritiro
                </Button>
                <Button variant="outline"
                  onClick={() => document.getElementById("come-funziona")?.scrollIntoView({ behavior: "smooth" })}
                  className="rounded-full px-8 py-6 text-lg border-2 border-sky-200 hover:bg-sky-50"
                  data-testid="hero-come-funziona-btn">
                  Come Funziona
                </Button>
              </div>
              <div className="flex items-center gap-6 mt-10 pt-8 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm text-slate-600">Consegna Puntuale</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <span className="text-sm text-slate-600">Acqua di Qualità</span>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-in-right hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1578496780896-7081cc23c111?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGl0YWxpYW4lMjBmYW1pbHklMjBkaW5pbmclMjB0YWJsZSUyMHdhdGVyfGVufDB8fHx8MTc3MDgyODg1Nnww&ixlib=rb-4.1.0&q=85"
                  alt="Famiglia felice a tavola"
                  className="rounded-3xl shadow-2xl w-full h-auto object-cover"
                  data-testid="hero-image" />
                <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 shadow-lg animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                      <Truck className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">Orario Consegna</p>
                      <p className="text-sm text-slate-500">10:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator hidden lg:block">
          <ChevronDown className="w-8 h-8 text-sky-400 animate-bounce" />
        </div>
      </section>

      <section id="come-funziona" className="py-20 lg:py-28 bg-white" data-testid="come-funziona-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Come Funziona</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Tre semplici passaggi per ricevere acqua fresca a casa tua</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <CalendarIcon className="w-8 h-8" />, n: 1, title: "Prenota Online", text: "Scegli data, ora e tipo di acqua (naturale o gassata) tramite il nostro form di prenotazione." },
              { icon: <GlassWater className="w-8 h-8" />, n: 2, title: "Prepara le Bottiglie", text: "Prepara le tue bottiglie vuote da 1,5L. Le ritireremo all'orario concordato." },
              { icon: <Truck className="w-8 h-8" />, n: 3, title: "Ricevi l'Acqua", text: "Passiamo a ritirare le bottiglie e te le restituiamo piene di acqua fresca!" },
            ].map((step) => (
              <div key={step.n} className={`feature-card text-center animate-fade-in-up stagger-${step.n}`} data-testid={`step-${step.n}-card`}>
                <div className="feature-icon mx-auto">{step.icon}</div>
                <div className="text-sky-500 font-bold text-sm mb-2">PASSO {step.n}</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-slate-600">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="prenota" className="py-20 lg:py-28 gradient-hero relative" data-testid="prenota-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Prenota il Tuo Ritiro</h2>
            <p className="text-lg text-slate-600">Compila il form per prenotare la consegna dell&apos;acqua</p>
          </div>

          {showSuccess ? (
            <div className="booking-card rounded-3xl p-8 text-center animate-fade-in-up" data-testid="success-message">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Prenotazione Confermata!
              </h3>
              <p className="text-slate-600 mb-6">
                Grazie per aver scelto Acqua Vicina. Ti aspettiamo all&apos;orario concordato.
              </p>
              {successData?.subscription_id && (
                <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 mb-6 text-left" data-testid="subscription-success-card">
                  <div className="flex items-center gap-2 mb-2">
                    <Repeat className="w-5 h-5 text-sky-600" />
                    <h4 className="font-semibold text-slate-800">Abbonamento attivato</h4>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">
                    Frequenza: <strong className="capitalize">{successData.ricorrenza}</strong>.
                    Conserva il codice qui sotto per disdire in qualsiasi momento:
                  </p>
                  <div className="flex items-center gap-2 bg-white rounded-xl p-2 border border-sky-200 mb-3">
                    <code className="flex-1 text-xs font-mono text-slate-700 truncate" data-testid="subscription-code">
                      {successData.subscription_id}
                    </code>
                    <Button
                      type="button" variant="outline" size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(successData.subscription_id);
                        toast.success("Codice copiato");
                      }}
                      data-testid="copy-subscription-code-btn"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <a
                    href={`/disdetta/${successData.subscription_id}`}
                    className="inline-block text-sm text-sky-600 hover:text-sky-800 underline"
                    data-testid="manage-subscription-link"
                  >
                    Gestisci o disdici l&apos;abbonamento →
                  </a>
                </div>
              )}
              <Button onClick={() => { setShowSuccess(false); setSuccessData(null); }} className="btn-primary text-white rounded-full px-8" data-testid="new-booking-btn">
                Nuova Prenotazione
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="booking-card rounded-3xl p-8 lg:p-12" data-testid="booking-form">
              <div className="grid gap-6">
                <div>
                  <Label htmlFor="nome" className="text-slate-700 font-medium mb-2 block">Nome Completo *</Label>
                  <Input id="nome" value={nome} onChange={(e) => setNome(e.target.value)}
                    placeholder="Mario Rossi" className="h-12 rounded-xl border-slate-200 focus:border-sky-500"
                    data-testid="input-nome" required />
                </div>

                <div>
                  <Label htmlFor="indirizzo" className="text-slate-700 font-medium mb-2 block">Indirizzo *</Label>
                  <Input id="indirizzo" value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)}
                    placeholder="Via Roma 1, Cascina (PI)" className="h-12 rounded-xl border-slate-200 focus:border-sky-500"
                    data-testid="input-indirizzo" required />
                  <p className="text-sm text-slate-500 mt-1 flex items-center gap-1" data-testid="hint-zona-cascina">
                    <MapPin className="w-4 h-4" />
                    Serviamo esclusivamente il comune di Cascina (PI). L&apos;indirizzo verrà verificato automaticamente.
                  </p>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium mb-3 block">Tipo di Acqua *</Label>
                  <div className="flex gap-4">
                    <button type="button" onClick={() => setTipoAcqua("naturale")}
                      className={`water-type-btn ${tipoAcqua === "naturale" ? "selected" : ""}`}
                      data-testid="btn-acqua-naturale">
                      <Droplets className="w-8 h-8 text-sky-500" />
                      <span className="font-medium text-slate-800">Naturale</span>
                    </button>
                    <button type="button" onClick={() => setTipoAcqua("gassata")}
                      className={`water-type-btn ${tipoAcqua === "gassata" ? "selected" : ""}`}
                      data-testid="btn-acqua-gassata">
                      <Sparkles className="w-8 h-8 text-sky-500" />
                      <span className="font-medium text-slate-800">Gassata</span>
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 font-medium mb-2 block">Data Ritiro *</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline"
                          className="w-full h-12 rounded-xl border-slate-200 justify-start text-left font-normal hover:bg-slate-50"
                          data-testid="btn-select-date">
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                          {dataConsegna ? format(dataConsegna, "d MMMM yyyy", { locale: it }) : <span className="text-slate-400">Seleziona data</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={dataConsegna}
                          onSelect={(date) => { setDataConsegna(date); setCalendarOpen(false); }}
                          disabled={disabledDays} locale={it} data-testid="calendar-picker" />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-slate-700 font-medium mb-2 block">Ora Ritiro *</Label>
                    <div className="grid grid-cols-4 gap-2" data-testid="time-slots-container">
                      {TIME_SLOTS.map((time) => {
                        const slotInfo = getSlotInfo(time);
                        const isUnavailable = dataConsegna && !slotInfo.disponibile;
                        return (
                          <button key={time} type="button"
                            onClick={() => !isUnavailable && setOraConsegna(time)}
                            disabled={isUnavailable}
                            className={`time-slot ${oraConsegna === time ? "selected" : ""} ${isUnavailable ? "opacity-50 cursor-not-allowed bg-red-50 border-red-200 text-red-400" : ""}`}
                            data-testid={`time-slot-${time.replace(":", "")}`}
                            title={isUnavailable ? "Orario al completo" : `${slotInfo.posti_disponibili} posti disponibili`}>
                            <span>{time}</span>
                            {dataConsegna && (
                              <span className={`text-xs block ${isUnavailable ? "text-red-400" : "text-slate-400"}`}>
                                {isUnavailable ? "Pieno" : `${slotInfo.posti_disponibili} posti`}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {dataConsegna && slotAvailability.length > 0 && (
                      <p className="text-xs text-slate-500 mt-2">
                        Massimo 4 prenotazioni per orario. Gli slot in rosso sono al completo.
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="telefono" className="text-slate-700 font-medium mb-2 block">Telefono (opzionale)</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input id="telefono" value={telefono} onChange={(e) => setTelefono(e.target.value)}
                        placeholder="+39 333 1234567"
                        className="h-12 rounded-xl border-slate-200 focus:border-sky-500 pl-10"
                        data-testid="input-telefono" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Riceverai conferma e promemoria via WhatsApp</p>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">Email (opzionale)</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="mario.rossi@example.com"
                        className="h-12 rounded-xl border-slate-200 focus:border-sky-500 pl-10"
                        data-testid="input-email" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Riceverai conferma e promemoria via email</p>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 font-medium mb-2 block flex items-center gap-2">
                    <Repeat className="w-4 h-4 text-sky-500" />
                    Abbonamento Ricorrente (opzionale)
                  </Label>
                  <Select value={ricorrenza} onValueChange={setRicorrenza}>
                    <SelectTrigger className="h-12 rounded-xl border-slate-200" data-testid="select-ricorrenza">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nessuna">Solo questa prenotazione</SelectItem>
                      <SelectItem value="settimanale">Ogni settimana</SelectItem>
                      <SelectItem value="quindicinale">Ogni 2 settimane</SelectItem>
                      <SelectItem value="mensile">Ogni mese</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">
                    Attiva un abbonamento per ricevere consegne automatiche. Potrai disdire in qualsiasi momento.
                  </p>
                </div>

                <Button type="submit" disabled={isSubmitting}
                  className="btn-primary text-white rounded-full h-14 text-lg mt-4"
                  data-testid="submit-booking-btn">
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Elaborazione...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Conferma Prenotazione
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="delivery-zone-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Zona di Consegna</h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Il nostro servizio è disponibile <strong>esclusivamente</strong> per i residenti
                del comune di <strong>Cascina, in provincia di Pisa</strong>.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Comune di Cascina</h4>
                    <p className="text-slate-600 text-sm">Provincia di Pisa, Toscana</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-2xl">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Orario Consegne</h4>
                    <p className="text-slate-600 text-sm">Dal Lunedì al Venerdì, 10:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="animate-slide-in-right">
              <img src="https://customer-assets.emergentagent.com/job_aqua-delivery-16/artifacts/m7hkt8up_Com.Cascina.JPG"
                alt="Comune di Cascina"
                className="rounded-3xl shadow-xl w-full h-auto object-cover"
                data-testid="delivery-image" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 gradient-hero" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Cosa Dicono i Nostri Clienti</h2>
            <p className="text-lg text-slate-600">La soddisfazione dei nostri clienti è la nostra priorità</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={t.id} className={`testimonial-card animate-fade-in-up stagger-${i + 1}`} data-testid={`testimonial-card-${i}`}>
                <div className="flex items-center gap-1 mb-4 star-rating">
                  {[...Array(t.valutazione)].map((_, j) => (<Star key={`star-${t.id}-${j}`} className="w-5 h-5 fill-current" />))}
                </div>
                <p className="text-slate-700 mb-4 italic">&quot;{t.testo}&quot;</p>
                <p className="font-semibold text-slate-900">{t.nome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="footer py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Logo size={36} />
              <span className="font-bold text-xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Acqua Vicina</span>
            </div>
            <div className="text-center md:text-left">
              <p className="text-slate-400 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Servizio disponibile solo a Cascina, Pisa
              </p>
            </div>
            <div className="text-slate-400 text-sm">
              © {new Date().getFullYear()} Acqua Vicina. Tutti i diritti riservati.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
